export type ServerActionResult<T> =
  | { data: T }
  | { error: { message: string } };

export class ExpectedError extends Error {
  statusCode?: number;
  /**
   * The platform `errorCode`. `message` already carries it when there is one,
   * but it also falls back to prose, so branch on this instead of the message.
   */
  errorCode?: string;
  /** The error envelope's `data`: per-field validation rows, `minZoom`, the offending file… */
  data?: unknown;

  constructor(
    message: string,
    statusCode?: number,
    details?: { errorCode?: string; data?: unknown }
  ) {
    super(message);
    this.name = 'ExpectedError';
    this.statusCode = statusCode;
    this.errorCode = details?.errorCode;
    this.data = details?.data;
  }
}

/**
 * Wraps a server action to handle errors gracefully.
 * If an `ExpectedError` occurs (thrown conditionally from fetch),
 * it returns an object with the error message instead of throwing
 * allowing error messages to be displayed in production
 * (React omits error messages returned from server in production builds for security reasons)
 * while hiding sensitive information for unexpected errors.
 */
export function createServerAction<Return, Args extends unknown[] = []>(
  callback: (...args: Args) => Promise<Return>
): (...args: Args) => Promise<ServerActionResult<Return>> {
  return async (...args: Args) => {
    try {
      const data = await callback(...args);
      return { data };
    } catch (error) {
      // if expected error, return safely to client
      if (error instanceof ExpectedError)
        return {
          error: { message: error.message },
        };
      // else, hide error details
      throw error;
    }
  };
}
